/**
 * ENG-10348 (ADR-0100, leg 2 of ENG-10344): what an agent may read back of its
 * own direct-chat session, and how a row in that read is classified.
 *
 * `NOTICE_KIND_AUDIENCE` answers one question: may the USER see a row. An agent
 * reading its own session is a different reader. The history route used to
 * exclude every notice, copying a filter written for a person, so an agent could
 * not see the record of what happened in its own session. On ENG-10341 the
 * approval-capture receipt that proved a diagnosis was invisible to the agent
 * asked to verify it, and it correctly concluded the evidence was not there.
 *
 * This module is that reader's rule, shared the way `history-limit.ts` is shared
 * so the host route and any later reader cannot disagree. The route computes the
 * classification server-side and sends the answer: the MCP ships inside the CLI
 * to a mixed-version fleet, and a host holding an older copy of the audience
 * table would otherwise mislabel any kind added after it was built.
 *
 * Pure (no node builtins), so it rides the barrel.
 */

import {
  isUserFacingNoticeKind,
  shouldHideNoticeFromUser,
  USER_FACING_NOTICE_KINDS,
  type KnownNoticeKind,
} from './notice-display.js';

/**
 * Notice kinds withheld from an agent's own history read. EMPTY, and that is a
 * recorded decision rather than a gap. The rule it applies:
 *
 *   The read is scoped to agent_id AND session_id, so every row is in this
 *   agent's own session. Every notice there was either delivered to this agent on
 *   its inbound rail (role='user') or posted into its session for its user
 *   (role='assistant'). Neither carries another principal's data by construction,
 *   and the route returns `payload.kind` only, never the payload. So nothing is
 *   withheld.
 *
 * A notice kind that DOES carry data this agent must not see (another
 * principal's content, a credential in `content`) belongs here, named, with its
 * reason beside it. Typed against `KnownNoticeKind`, so an entry cannot name a
 * kind that does not exist.
 */
export const AGENT_HISTORY_WITHHELD_NOTICE_KINDS: readonly KnownNoticeKind[] = [];

/** The three row fields the classification reads. `payloadKind` is `payload.kind`. */
export interface AgentHistoryRowFacts {
  kind: string | null | undefined;
  payloadKind: string | null | undefined;
  role: string;
}

function nonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.length > 0;
}

/** Is this row withheld from the agent's own history read? */
export function isWithheldFromAgentHistory(row: Pick<AgentHistoryRowFacts, 'payloadKind'>): boolean {
  return (
    nonEmptyString(row.payloadKind) &&
    (AGENT_HISTORY_WITHHELD_NOTICE_KINDS as readonly string[]).includes(row.payloadKind)
  );
}

/**
 * Is this row a NOTICE rather than a turn in the conversation?
 *
 * Not the `kind` column alone. That column is not the authority (see
 * notice-display.ts), and at least one writer stores platform instructions as
 * `kind='message'` with an agent-audience `payload.kind`: the `waiting_response`
 * default arm (waiting-response.ts, tracked as ENG-10353). A row is a notice when
 * `kind` says so, OR when it carries a `payload.kind` that is not user-facing.
 *
 * The second clause is deliberately "not user-facing" rather than "has a payload
 * kind". A user-facing payload on a message row is the agent's own form card
 * (`form_request_render`), which is a turn the agent wrote and must stay one. A
 * kind this build has never heard of is treated as a notice, the same
 * hide-by-default direction the audience table takes.
 */
export function isAgentHistoryNotice(row: Pick<AgentHistoryRowFacts, 'kind' | 'payloadKind'>): boolean {
  if (row.kind === 'notice') return true;
  return nonEmptyString(row.payloadKind) && !isUserFacingNoticeKind(row.payloadKind);
}

/**
 * The PostgREST `or=` filter that keeps exactly the rows `isAgentHistoryNotice`
 * calls turns — the default history read's half of ENG-10353.
 *
 * WHY THE FILTER IS SQL AND THE LABELLING IS CODE. The default read must not
 * RETURN a notice at all, because the only consumer is the MCP shipped in the
 * CLI to a mixed-version fleet, and every build older than ENG-10348 renders a
 * `role='user'` row as "User" no matter what the row says about itself. A label
 * the reader ignores fixes nothing; withholding the row does. And it has to be
 * SQL rather than a post-filter, because `limit` is applied by the database: a
 * filter in code would silently return fewer rows than asked for.
 *
 * WHY IT IS DERIVED, NOT WRITTEN OUT. The list is generated from
 * `USER_FACING_NOTICE_KINDS`, itself generated from `NOTICE_KIND_AUDIENCE`, so
 * a kind added to the table cannot leave this filter behind. A hand-copied list
 * is the drift this module exists to prevent.
 *
 * THE NULL TRAP, measured rather than reasoned about. The obvious spelling,
 * `payload->>kind=not.in.(<agent kinds>)`, drops EVERY ordinary turn: those rows
 * have no `payload.kind`, `NULL NOT IN (…)` is NULL, and PostgREST keeps only
 * rows where the predicate is true. Against dev it returned 1 row where the
 * unfiltered read returned 200 — an empty transcript that looks like a working
 * query. So the filter is written the null-safe way round: keep a row when it
 * has NO payload kind, or a user-facing one. `''` is listed beside null because
 * `payload->>kind` of `{"kind":""}` is a non-null empty string, which SQL would
 * otherwise drop while {@link isAgentHistoryNotice} keeps it.
 *
 * Unquoted list members are safe only while every kind is `[a-z0-9_]`; the
 * ENG-10353 test asserts that, so a kind with a comma or a quote fails there
 * rather than silently changing what this filter matches.
 */
export const AGENT_HISTORY_TURNS_ONLY_OR_FILTER: string = [
  'payload->>kind.is.null',
  `payload->>kind.in.("",${[...USER_FACING_NOTICE_KINDS].sort().join(',')})`,
].join(',');

/**
 * Was this row delivered to the agent?
 *
 * `role='user'` is the inbound rail the agent's poll claims from, not a claim
 * about authorship: a broker grant notice is `role='user'` because that is how
 * it reaches the agent. `role='assistant'` rows were posted into the session
 * for the user and never delivered to the agent.
 */
export function wasDeliveredToAgent(row: Pick<AgentHistoryRowFacts, 'role'>): boolean {
  return row.role === 'user';
}

/**
 * Does the user-facing Direct Chat render this row?
 *
 * The same rule the console applies (`shouldHideNoticeFromUser`), so the answer
 * cannot drift from what the console shows. It reads only `payload.kind`, which
 * is why reconstructing a payload from the kind alone is exact. A notice with no
 * `payload.kind` is shown, as it is in the console.
 *
 * This is a statement about the rendering rule, not a claim that anyone saw the
 * row: per-user scoping and synthetic-probe sessions are separate filters on
 * the console's own reads.
 */
export function isShownInUserChat(row: Pick<AgentHistoryRowFacts, 'payloadKind'>): boolean {
  return !shouldHideNoticeFromUser({
    payload: nonEmptyString(row.payloadKind) ? { kind: row.payloadKind } : null,
  });
}
